import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { campaignsAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { SortableTableHead } from '../components/ui/SortableTableHead';
import { useSortableTable } from '../hooks/useSortableTable';
import {
  Loader2, Plus, Zap, Play, Pause, Trash2, Search, ArrowRight,
  Clock, Users, Mail, ChevronRight, ArrowLeft, FileText, Check,
} from 'lucide-react';
import { MobileFAB } from '../components/MobileFAB';
import { toast } from 'sonner';
import { blocksToHtml } from '../components/BlockEmailEditor';

const AUTOMATION_SORT_COLUMNS = [
  { key: 'name', type: 'text' },
  { key: 'trigger_event', type: 'text' },
  { key: 'trigger_delay_days', type: 'number' },
  { key: 'status', type: 'status' },
  { key: 'email_count', type: 'number' },
  { key: 'updated_at', type: 'date' },
];

const TRIGGER_EVENTS = [
  { value: 'estimate_created', label: 'Estimate Created', desc: 'When a new estimate is created for a customer' },
  { value: 'estimate_sent', label: 'Estimate Sent', desc: 'When an estimate is sent to the customer' },
  { value: 'estimate_approved', label: 'Estimate Approved', desc: 'When a customer approves an estimate' },
  { value: 'estimate_declined', label: 'Estimate Declined', desc: 'When a customer declines an estimate' },
  { value: 'job_completed', label: 'Job Completed', desc: 'When a job is marked as completed' },
  { value: 'job_scheduled', label: 'Job Scheduled', desc: 'When a new job is scheduled' },
  { value: 'invoice_sent', label: 'Invoice Sent', desc: 'When an invoice is sent to the customer' },
  { value: 'invoice_paid', label: 'Invoice Paid', desc: 'When a customer pays an invoice' },
  { value: 'invoice_overdue', label: 'Invoice Overdue', desc: 'When an invoice becomes overdue' },
  { value: 'customer_created', label: 'New Customer', desc: 'When a new customer is created' },
];

// ─── Pre-built templates with full email content ───
const TEMPLATES = [
  {
    key: 'unsold_estimate',
    name: 'Unsold Estimate Follow-Up',
    trigger: 'estimate_sent',
    delay: 1,
    desc: 'Recover unsold estimates with a 3-email drip sequence.',
    emails: [
      {
        delay_days: 1, subject: 'Just checking in on your estimate',
        blocks: [
          { type: 'text', content: '<p>Hi {{customer_first_name}},</p><p>Thanks for requesting an estimate from us. I wanted to follow up and see if you had any questions about the work we proposed.</p><p>We\'re happy to walk through the details or adjust anything to fit your needs.</p>', align: 'left' },
          { type: 'button', text: 'View Your Estimate', url: '{{estimate_link}}', bgColor: '#2563eb', textColor: '#ffffff', align: 'center', borderRadius: '6' },
          { type: 'spacer', height: '10' },
          { type: 'text', content: '<p>Best regards,<br/>{{company_name}}</p>', align: 'left' },
        ],
      },
      {
        delay_days: 3, subject: 'Your estimate is still waiting',
        blocks: [
          { type: 'text', content: '<p>Hi {{customer_first_name}},</p><p>I wanted to make sure your estimate didn\'t get lost in the shuffle. We put it together based on what we discussed and I think you\'ll be happy with the value.</p><p>If the timing isn\'t right or the scope needs adjusting, just let me know — I\'m happy to help.</p>', align: 'left' },
          { type: 'button', text: 'Review Your Estimate', url: '{{estimate_link}}', bgColor: '#2563eb', textColor: '#ffffff', align: 'center', borderRadius: '6' },
          { type: 'spacer', height: '10' },
          { type: 'text', content: '<p>— {{company_name}}<br/>{{company_phone}}</p>', align: 'left' },
        ],
      },
      {
        delay_days: 7, subject: 'Last chance: your estimate expires soon',
        blocks: [
          { type: 'text', content: '<p>Hi {{customer_first_name}},</p><p>This is a final reminder that the estimate we prepared for you will be expiring soon. If you\'d like to move forward, now is the time to lock in the current pricing.</p><p>No pressure — but I didn\'t want you to miss out if this is something you still need done.</p>', align: 'left' },
          { type: 'button', text: 'Approve Your Estimate', url: '{{estimate_link}}', bgColor: '#16a34a', textColor: '#ffffff', align: 'center', borderRadius: '6' },
          { type: 'spacer', height: '10' },
          { type: 'text', content: '<p>Thanks,<br/>{{company_name}}</p><p style="font-size:12px;color:#6b7280;"><a href="{{unsubscribe_url}}">Unsubscribe</a></p>', align: 'left' },
        ],
      },
    ],
  },
  {
    key: 'review_request',
    name: 'Review Request',
    trigger: 'job_completed',
    delay: 1,
    desc: 'Ask for a review 1 day after a job is completed.',
    emails: [
      {
        delay_days: 1, subject: 'How did we do?',
        blocks: [
          { type: 'text', content: '<p>Hi {{customer_first_name}},</p><p>Thank you for choosing {{company_name}}! We hope you\'re happy with the work.</p><p>Would you take a minute to leave us a quick review? It helps other homeowners find quality service and means a lot to our team.</p>', align: 'left' },
          { type: 'button', text: 'Leave a Review', url: '{{google_review_url}}', bgColor: '#f59e0b', textColor: '#ffffff', align: 'center', borderRadius: '6' },
          { type: 'spacer', height: '10' },
          { type: 'text', content: '<p>Thank you!<br/>The {{company_name}} Team</p><p style="font-size:12px;color:#6b7280;"><a href="{{unsubscribe_url}}">Unsubscribe</a></p>', align: 'left' },
        ],
      },
    ],
  },
  {
    key: 'maintenance_reminder',
    name: 'Maintenance Reminder',
    trigger: 'job_completed',
    delay: 180,
    desc: 'Remind customers to book maintenance 6 months after their last job.',
    emails: [
      {
        delay_days: 180, subject: 'Time for a maintenance check-up?',
        blocks: [
          { type: 'text', content: '<p>Hi {{customer_first_name}},</p><p>It\'s been about 6 months since we last worked on your property. Regular maintenance helps prevent costly repairs and keeps everything running smoothly.</p><p>Would you like to schedule a check-up? We can take a look and let you know if anything needs attention.</p>', align: 'left' },
          { type: 'button', text: 'Schedule Maintenance', url: 'tel:{{company_phone}}', bgColor: '#2563eb', textColor: '#ffffff', align: 'center', borderRadius: '6' },
          { type: 'spacer', height: '10' },
          { type: 'text', content: '<p>— {{company_name}}<br/>{{company_phone}}</p><p style="font-size:12px;color:#6b7280;"><a href="{{unsubscribe_url}}">Unsubscribe</a></p>', align: 'left' },
        ],
      },
    ],
  },
  {
    key: 'customer_reactivation',
    name: 'Customer Reactivation',
    trigger: 'job_completed',
    delay: 365,
    desc: 'Re-engage dormant customers 12 months after their last job.',
    emails: [
      {
        delay_days: 365, subject: 'We miss you, {{customer_first_name}}!',
        blocks: [
          { type: 'text', content: '<p>Hi {{customer_first_name}},</p><p>It\'s been a while since we last worked together and we wanted to check in. Is there anything around the house or property that needs attention?</p><p>As a valued past customer, we\'d love to help again. Just reply to this email or give us a call.</p>', align: 'left' },
          { type: 'button', text: 'Get in Touch', url: 'tel:{{company_phone}}', bgColor: '#7c3aed', textColor: '#ffffff', align: 'center', borderRadius: '6' },
          { type: 'spacer', height: '10' },
          { type: 'text', content: '<p>Looking forward to hearing from you,<br/>{{company_name}}<br/>{{company_phone}}</p><p style="font-size:12px;color:#6b7280;"><a href="{{unsubscribe_url}}">Unsubscribe</a></p>', align: 'left' },
        ],
      },
      {
        delay_days: 370, subject: 'A quick reminder from {{company_name}}',
        blocks: [
          { type: 'text', content: '<p>Hi {{customer_first_name}},</p><p>Just a quick follow-up — I reached out last week to see if you needed any work done. No pressure at all, just wanted to make sure you saw my note.</p><p>We\'re here whenever you need us.</p>', align: 'left' },
          { type: 'text', content: '<p>Best,<br/>{{company_name}}</p><p style="font-size:12px;color:#6b7280;"><a href="{{unsubscribe_url}}">Unsubscribe</a></p>', align: 'left' },
        ],
      },
    ],
  },
];

export default function Automations() {
  const navigate = useNavigate();
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // list | pick_template | create_custom
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [audiences, setAudiences] = useState([]);
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [autoRes, audRes] = await Promise.all([
        campaignsAPI.list({ campaign_type: 'automated' }),
        campaignsAPI.listAudiences(),
      ]);
      setAutomations(autoRes.data);
      setAudiences(audRes.data);
    } catch { toast.error('Failed to load automations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = automations.filter(a =>
    !search || a.name.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted: sortedAutomations, sortKey, sortDir, onSort } = useSortableTable(filtered, { columns: AUTOMATION_SORT_COLUMNS });

  const handleToggle = async (automation) => {
    const newStatus = automation.status === 'active' ? 'paused' : 'active';
    try {
      await campaignsAPI.update(automation.id, { status: newStatus });
      toast.success(`Automation ${newStatus === 'active' ? 'activated' : 'paused'}`);
      fetchData();
    } catch { toast.error('Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this automation?')) return;
    try {
      await campaignsAPI.delete(id);
      toast.success('Automation deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleUseTemplate = async (template) => {
    setCreating(true);
    try {
      // 1. Create the campaign
      const res = await campaignsAPI.create({
        name: template.name,
        description: template.desc,
        campaign_type: 'automated',
        trigger_type: 'event',
        trigger_event: template.trigger,
        trigger_delay_days: template.delay,
      });
      const campaignId = res.data.id;

      // 2. Add prefilled emails with content
      for (let i = 0; i < template.emails.length; i++) {
        const email = template.emails[i];
        const blocks = (email.blocks || []).map((b, bi) => ({ ...b, id: `${Date.now()}-${bi}` }));
        const html = blocksToHtml(blocks);
        await campaignsAPI.addEmail(campaignId, {
          email_order: i,
          delay_days: email.delay_days,
          subject: email.subject,
          html_content: html,
        });
      }

      toast.success(`"${template.name}" created with ${template.emails.length} email${template.emails.length !== 1 ? 's' : ''}!`);
      navigate(`/marketing/campaigns/${campaignId}`);
    } catch (err) {
      toast.error('Failed to create automation from template');
    } finally {
      setCreating(false);
    }
  };

  // ─── Template picker view ───
  if (mode === 'pick_template') {
    return (
      <Layout>
        <div className="space-y-6" data-testid="template-picker">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setMode('list')} data-testid="back-to-list">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">New Automation</h1>
              <p className="text-sm text-muted-foreground mt-1">Choose a template or start from scratch</p>
            </div>
          </div>

          {/* Templates grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {TEMPLATES.map(t => {
              const trigger = TRIGGER_EVENTS.find(te => te.value === t.trigger);
              return (
                <Card
                  key={t.key}
                  className={`cursor-pointer transition-all hover:border-primary/40 ${selectedTemplate === t.key ? 'border-primary ring-1 ring-primary/20' : ''}`}
                  onClick={() => setSelectedTemplate(selectedTemplate === t.key ? null : t.key)}
                  data-testid={`template-card-${t.key}`}
                >
                  <CardContent className="pt-5 pb-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-sm font-semibold">{t.name}</h3>
                        <p className="text-xs text-muted-foreground mt-1">{t.desc}</p>
                      </div>
                      {selectedTemplate === t.key && (
                        <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center shrink-0 ml-2">
                          <Check className="w-3.5 h-3.5 text-primary-foreground" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-[11px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Zap className="w-3 h-3" /> {trigger?.label}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {t.delay}d delay
                      </span>
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" /> {t.emails.length} email{t.emails.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {/* Email preview */}
                    <div className="mt-3 space-y-1">
                      {t.emails.map((e, i) => (
                        <div key={i} className="flex items-center gap-2 text-[10px] text-muted-foreground pl-1">
                          <div className="w-4 h-4 rounded-full border flex items-center justify-center text-[8px] font-bold shrink-0">{i + 1}</div>
                          <span>Day {e.delay_days}: <span className="font-medium text-foreground">{e.subject}</span></span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2">
            <Button variant="outline" onClick={() => { setMode('create_custom'); setSelectedTemplate(null); }} data-testid="start-from-scratch">
              <FileText className="w-4 h-4 mr-2" /> Start from Scratch
            </Button>
            <Button
              disabled={!selectedTemplate || creating}
              onClick={() => {
                const t = TEMPLATES.find(x => x.key === selectedTemplate);
                if (t) handleUseTemplate(t);
              }}
              data-testid="use-template-btn"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Zap className="w-4 h-4 mr-2" />}
              Use This Template
            </Button>
          </div>
        </div>
      </Layout>
    );
  }

  // ─── Custom create form ───
  if (mode === 'create_custom') {
    return (
      <Layout>
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => setMode('pick_template')}>
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <h1 className="text-2xl font-bold">New Automation — Custom</h1>
          </div>
          <CreateAutomation audiences={audiences} onCancel={() => setMode('list')} />
        </div>
      </Layout>
    );
  }

  // ─── Main list view ───
  return (
    <Layout>
      <div className="space-y-6" data-testid="automations-page">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="automations-title">
              <Zap className="w-6 h-6" />
              Automations
            </h1>
            <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
              {automations.length} trigger-based email sequence{automations.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button onClick={() => setMode('pick_template')} data-testid="create-automation-btn" className="hidden md:inline-flex">
            <Plus className="w-4 h-4 mr-2" /> New Automation
          </Button>
        </div>

        <div className="flex items-center gap-2 flex-1 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search automations..." className="pl-10"
              data-testid="search-automations"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="border rounded-lg p-12 text-center">
            <Zap className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <h3 className="text-sm font-medium text-muted-foreground">No automations yet</h3>
            <p className="text-xs text-muted-foreground/60 mt-1 mb-4">Create your first automation to send targeted emails when key events happen</p>
            <Button onClick={() => setMode('pick_template')}>
              <Plus className="w-4 h-4 mr-2" /> New Automation
            </Button>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="border rounded-lg overflow-x-auto hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="name" onSort={onSort} className="min-w-[180px]">Name</SortableTableHead>
                    <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="trigger_event" onSort={onSort} className="min-w-[140px]">Trigger</SortableTableHead>
                    <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="trigger_delay_days" onSort={onSort} className="w-24 text-center">Delay</SortableTableHead>
                    <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="status" onSort={onSort} className="w-28 text-center">Status</SortableTableHead>
                    <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="email_count" onSort={onSort} className="w-24 text-center">Emails</SortableTableHead>
                    <TableHead className="w-36">Audience</TableHead>
                    <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="updated_at" onSort={onSort} className="w-36">Updated</SortableTableHead>
                    <TableHead className="w-40 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedAutomations.map(a => {
                    const trigger = TRIGGER_EVENTS.find(t => t.value === a.trigger_event);
                    return (
                      <TableRow key={a.id} data-testid={`automation-row-${a.id}`}>
                        <TableCell>
                          <span className="font-medium text-sm">{a.name}</span>
                          {a.description && (
                            <p className="text-xs text-muted-foreground truncate max-w-[200px]">{a.description}</p>
                          )}
                        </TableCell>
                        <TableCell><span className="text-sm">{trigger?.label || a.trigger_event || '—'}</span></TableCell>
                        <TableCell className="text-center"><span className="text-sm text-muted-foreground">{a.trigger_delay_days > 0 ? `${a.trigger_delay_days}d` : 'Imm.'}</span></TableCell>
                        <TableCell className="text-center">
                          <Badge variant={a.status === 'active' ? 'default' : 'secondary'} className={`text-xs ${a.status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}`}>{a.status}</Badge>
                        </TableCell>
                        <TableCell className="text-center"><span className="text-sm text-muted-foreground">{a.email_count}</span></TableCell>
                        <TableCell><span className="text-xs text-muted-foreground truncate block max-w-[130px]">{a.audience_name || 'All customers'}</span></TableCell>
                        <TableCell><span className="text-xs text-muted-foreground">{a.updated_at ? new Date(a.updated_at).toLocaleDateString() : '—'}</span></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant={a.status === 'active' ? 'outline' : 'default'} size="sm" className="h-7 text-xs" onClick={() => handleToggle(a)} data-testid={`toggle-automation-${a.id}`}>
                              {a.status === 'active' ? <><Pause className="w-3 h-3 mr-1" /> Pause</> : <><Play className="w-3 h-3 mr-1" /> Activate</>}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => navigate(`/marketing/campaigns/${a.id}`)} data-testid={`edit-automation-${a.id}`}>Edit</Button>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => handleDelete(a.id)} data-testid={`delete-automation-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {/* Mobile card layout */}
            <div className="md:hidden space-y-2">
              {sortedAutomations.map(a => {
                const trigger = TRIGGER_EVENTS.find(t => t.value === a.trigger_event);
                return (
                  <Card key={a.id} data-testid={`automation-card-${a.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate">{a.name}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{trigger?.label || a.trigger_event || '—'}</p>
                        </div>
                        <Badge variant={a.status === 'active' ? 'default' : 'secondary'} className={`text-xs shrink-0 ${a.status === 'active' ? 'bg-green-100 text-green-800 hover:bg-green-100' : ''}`}>{a.status}</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span>Delay: {a.trigger_delay_days > 0 ? `${a.trigger_delay_days}d` : 'Imm.'}</span>
                        <span>{a.email_count} emails</span>
                      </div>
                      <div className="flex items-center gap-1 mt-3 pt-3 border-t">
                        <Button variant={a.status === 'active' ? 'outline' : 'default'} size="sm" className="h-7 text-xs flex-1" onClick={() => handleToggle(a)} data-testid={`toggle-automation-${a.id}`}>
                          {a.status === 'active' ? <><Pause className="w-3 h-3 mr-1" /> Pause</> : <><Play className="w-3 h-3 mr-1" /> Activate</>}
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs flex-1" onClick={() => navigate(`/marketing/campaigns/${a.id}`)} data-testid={`edit-automation-${a.id}`}>Edit</Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive hover:text-destructive shrink-0" onClick={() => handleDelete(a.id)} data-testid={`delete-automation-${a.id}`}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
      </div>
      <MobileFAB onClick={() => setMode('pick_template')} data-testid="mobile-fab-automation" />
    </Layout>
  );
}


function CreateAutomation({ audiences, onCancel }) {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [triggerEvent, setTriggerEvent] = useState('');
  const [delayDays, setDelayDays] = useState(0);
  const [audienceId, setAudienceId] = useState('');

  const handleCreate = async () => {
    if (!name.trim() || !triggerEvent) {
      toast.error('Name and trigger event are required');
      return;
    }
    setSaving(true);
    try {
      const res = await campaignsAPI.create({
        name: name.trim(),
        description: description.trim() || null,
        campaign_type: 'automated',
        trigger_type: 'event',
        trigger_event: triggerEvent,
        trigger_delay_days: delayDays,
        audience_id: audienceId && audienceId !== '__all__' ? audienceId : null,
      });
      toast.success('Automation created! Add emails to complete setup.');
      navigate(`/marketing/campaigns/${res.data.id}`);
    } catch { toast.error('Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-primary/20" data-testid="create-automation-form">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Zap className="w-4 h-4" /> Custom Automation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium">Name</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Post-Job Review Request" className="mt-1" data-testid="automation-name" />
          </div>
          <div>
            <label className="text-xs font-medium">Trigger Event</label>
            <Select value={triggerEvent} onValueChange={setTriggerEvent}>
              <SelectTrigger className="mt-1" data-testid="automation-trigger"><SelectValue placeholder="Select event..." /></SelectTrigger>
              <SelectContent>
                {TRIGGER_EVENTS.map(t => (
                  <SelectItem key={t.value} value={t.value}>
                    <span className="flex items-center gap-2">
                      <span>{t.label}</span>
                      <span className="text-muted-foreground text-[10px]">— {t.desc}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium">Delay (days)</label>
            <Input type="number" min={0} value={delayDays} onChange={e => setDelayDays(parseInt(e.target.value) || 0)} className="mt-1" data-testid="automation-delay" />
            <p className="text-[9px] text-muted-foreground mt-0.5">Days to wait after the trigger</p>
          </div>
          <div>
            <label className="text-xs font-medium">Audience (optional)</label>
            <Select value={audienceId || '__all__'} onValueChange={setAudienceId}>
              <SelectTrigger className="mt-1" data-testid="automation-audience"><SelectValue placeholder="All customers" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All customers</SelectItem>
                {audiences.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium">Description</label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional..." className="mt-1" />
          </div>
        </div>

        {triggerEvent && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 text-xs">
            <Zap className="w-4 h-4 text-amber-500" />
            <span>When</span>
            <Badge variant="outline">{TRIGGER_EVENTS.find(t => t.value === triggerEvent)?.label}</Badge>
            {delayDays > 0 && <><ArrowRight className="w-3 h-3" /><span>wait {delayDays} day{delayDays !== 1 ? 's' : ''}</span></>}
            <ArrowRight className="w-3 h-3" />
            <span>send email sequence</span>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>
          <Button size="sm" onClick={handleCreate} disabled={saving} data-testid="save-automation-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Zap className="w-3.5 h-3.5 mr-1" />}
            Create Automation
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
